import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';

interface IOutlineNode {
  label: string;
  key: string;
  type: 'section' | 'block' | 'field';
  icon: string;
  field: FormlyFieldConfig;
  children: IOutlineNode[];
  expanded: boolean;
}

@Component({
  selector: 'irembogov-fb-form-outline',
  templateUrl: './fb-form-outline.component.html',
  styleUrls: ['./fb-form-outline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbFormOutlineComponent implements OnInit, OnDestroy {
  @Input() visible = false;

  tree: IOutlineNode[] = [];
  selectedFieldKey: string | null = null;

  private destroy$ = new Subject<void>();
  private formBuilderService: FormBuilderService;

  constructor(private injector: Injector, private cdr: ChangeDetectorRef) {
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  ngOnInit(): void {
    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.buildTree(data.fields);
        this.cdr.markForCheck();
      });

    this.formBuilderService.configureFieldAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(field => {
        this.selectedFieldKey = field?.key?.toString() || null;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleNode(node: IOutlineNode, event?: Event): void {
    // Keep the click inside the outline: the canvas closes the outline on any
    // outside document click, and a rebuild can detach this node mid-event.
    event?.stopPropagation();
    node.expanded = !node.expanded;
    this.cdr.markForCheck();
  }

  selectNode(node: IOutlineNode, event?: Event): void {
    // Prevent the canvas' document:click handler from closing the outline.
    event?.stopPropagation();
    if (node.type === 'field') {
      // Selecting the field highlights it and the field wrapper scrolls itself
      // into view — reliable even for hidden / display-rule fields.
      this.formBuilderService.configureFieldAction$.next(node.field);
    }
  }

  isSelected(node: IOutlineNode): boolean {
    return this.selectedFieldKey !== null && node.key === this.selectedFieldKey;
  }

  trackByNodeKey(index: number, node: IOutlineNode): string {
    return node.key;
  }

  private buildTree(fields: FormlyFieldConfig[]): void {
    this.tree = [];
    if (!fields?.length || !fields[0]?.fieldGroup) return;

    const sections = fields[0].fieldGroup;
    this.tree = sections.map((section, si) =>
      this.buildNode(section, 0, `section-${si}`)
    );
  }

  // Recursively build the outline so every field shows regardless of nesting
  // depth (sections > blocks > groups/repeaters > fields).
  private buildNode(
    field: FormlyFieldConfig,
    depth: number,
    fallbackId: string
  ): IOutlineNode {
    // Descend into a container's fieldGroup, or a repeater's fieldArray template.
    const fieldArrayGroup = (field.fieldArray as FormlyFieldConfig | undefined)
      ?.fieldGroup;
    const childFields: FormlyFieldConfig[] = field.fieldGroup?.length
      ? field.fieldGroup
      : fieldArrayGroup?.length
      ? fieldArrayGroup
      : [];

    const children = childFields.map((child, i) =>
      this.buildNode(child, depth + 1, `${fallbackId}-${i}`)
    );
    const hasChildren = children.length > 0;

    let type: IOutlineNode['type'];
    let icon: string;
    if (depth === 0) {
      type = 'section';
      icon = 'fa-solid fa-layer-group';
    } else if (depth === 1) {
      type = 'block';
      icon = 'fa-solid fa-cube';
    } else if (hasChildren) {
      type = 'block';
      icon = 'fa-solid fa-folder';
    } else {
      type = 'field';
      icon = 'fa-solid fa-input-text';
    }

    return {
      // Use the field label; fall back to the key when it's blank/whitespace.
      label:
        ((field.props?.['label'] as string) || '').trim() ||
        field.key?.toString() ||
        fallbackId,
      key: field.key?.toString() || field.id || fallbackId,
      type,
      icon,
      field,
      children,
      expanded: true,
    };
  }
}
